import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Section } from '../PageShell.tsx';
import { useConfirm } from '../ConfirmDialog.tsx';
import { Modal } from '../Modal.tsx';
import { Avatar, EmptyState, Spinner } from '../ui.tsx';
import { PlusIcon, TrashIcon } from '../icons.tsx';
import { ApiRequestError, api } from '../../lib/api.ts';
import { connectFacebook } from '../../lib/facebook.ts';
import { useAuth } from '../../lib/auth.tsx';
import { SaveRow, saveError, saveFailureMessage, useSaved } from './shared.tsx';
import type { SettingsDto } from './shared.tsx';

interface IncomingEmailRow {
  id: number;
  address: string;
}

interface FacebookPageRow {
  id: number;
  title: string;
  picture: string | null;
}

export function ChannelsTab({ settings }: { settings: SettingsDto }): React.JSX.Element {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { refresh } = useAuth();

  const [twilio, setTwilio] = useState({
    accountSid: settings.chat.twilio?.accountSid ?? '',
    authToken: '',
    phoneNumber: settings.chat.twilio?.phoneNumber ?? '',
  });
  const [twilioError, setTwilioError] = useState<string | null>(null);
  const [twilioSaved, markTwilioSaved] = useSaved();

  const [inbound, setInbound] = useState({ username: '', domain: '' });
  const [inboundOpen, setInboundOpen] = useState(false);

  const emails = useQuery({
    queryKey: ['settings', 'incoming-emails'],
    queryFn: () => api.get<{ items: IncomingEmailRow[] }>('/settings/incoming-emails'),
  });

  const [facebookNote, setFacebookNote] = useState<string | null>(null);

  const facebook = useQuery({
    queryKey: ['settings', 'facebook'],
    queryFn: () =>
      api.get<{
        configured: boolean;
        appId: string | null;
        apiVersion: string;
        items: FacebookPageRow[];
      }>('/settings/facebook'),
  });

  const saveTwilio = useMutation({
    mutationFn: () => api.put('/settings/twilio', twilio),
    onSuccess: () => {
      setTwilioError(null);
      markTwilioSaved();
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Connecting a number is what puts SMS in front of every agent; `me`
      // carries that flag, so it has to be re-read rather than waited out.
      void refresh();
    },
    onError: (caught: unknown) => {
      setTwilioError(saveFailureMessage(caught));
    },
  });

  const disconnectTwilio = useMutation({
    mutationFn: () => api.delete('/settings/twilio'),
    onSuccess: () => {
      setTwilioError(null);
      setTwilio({ accountSid: '', authToken: '', phoneNumber: '' });
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      // The composer offers SMS off the back of this, and `me` is where it
      // reads that from — stale, an agent keeps being shown a channel the
      // workspace no longer has.
      void refresh();
    },
    onError: (caught: unknown) => {
      setTwilioError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong');
    },
  });

  const addEmail = useMutation({
    mutationFn: () => api.post('/settings/incoming-emails', inbound),
    onSuccess: () => {
      setInbound({ username: '', domain: '' });
      setInboundOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'incoming-emails'] });
    },
  });

  const removeEmail = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/incoming-emails/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'incoming-emails'] });
    },
  });

  /**
   * Facebook Login, then hand the token to the server.
   *
   * The consent dialog has to be opened by the browser — the server never sees
   * the person's Facebook password and only ever receives the short-lived token
   * it then exchanges for a long-lived one.
   */
  const connectFacebookPages = useMutation({
    mutationFn: async () => {
      const config = facebook.data;
      if (!config?.appId) throw new Error('Messenger is not configured on this server');

      const auth = await connectFacebook(config.appId, config.apiVersion);
      if (!auth) return null;

      return api.post<{ connected: number; conflicts: string[] }>('/settings/facebook/connect', {
        accessToken: auth.accessToken,
        userId: auth.userID,
      });
    },
    onError: (error) => {
      setFacebookNote(error instanceof Error ? error.message : 'Could not connect to Facebook.');
    },
    onSuccess: (result) => {
      // Closing the dialog is a decision, not a failure; say nothing.
      if (!result) return;

      const parts = [
        `${String(result.connected)} page${result.connected === 1 ? '' : 's'} connected`,
      ];

      if (result.conflicts.length > 0) {
        parts.push(
          `${result.conflicts.join(', ')} ${result.conflicts.length === 1 ? 'is' : 'are'} already connected to another workspace, so ${result.conflicts.length === 1 ? 'it was' : 'they were'} left alone`,
        );
      }

      setFacebookNote(`${parts.join('. ')}.`);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'facebook'] });
    },
  });

  const removePage = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/facebook/${String(id)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', 'facebook'] });
    },
  });

  return (
    <>
      <Section
        title="SMS via Twilio"
        description="Saving points the number's webhook at this server, which also verifies the credentials."
        actions={
          settings.chat.twilio?.configured ? (
            <button
              type="button"
              className="lg-btn-ghost hover:text-rose-600"
              disabled={disconnectTwilio.isPending}
              onClick={() => {
                void confirm({
                  title: 'Disconnect Twilio?',
                  message:
                    'This workspace stops sending and receiving SMS, and agents lose SMS as a reply channel. The number itself is untouched at Twilio — its webhook keeps pointing here until you change it there or connect it again.',
                  confirmLabel: 'Disconnect',
                  destructive: true,
                }).then((ok) => {
                  if (ok) disconnectTwilio.mutate();
                });
              }}
            >
              {disconnectTwilio.isPending ? <Spinner /> : <TrashIcon className="h-4 w-4" />}
              Disconnect
            </button>
          ) : null
        }
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="lg-label" htmlFor="twilio-sid">
              Account SID
            </label>
            {/* A text field next to a password field reads as a sign-in form
                to every browser and password manager, so this one was being
                filled with a saved email address. `new-password` is what
                actually suppresses that in Chrome — plain `off` is advisory and
                widely ignored — and the data attributes cover 1Password,
                LastPass and Bitwarden. */}
            <input
              id="twilio-sid"
              name="twilio-account-sid"
              className="lg-input"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              spellCheck={false}
              placeholder="AC…"
              value={twilio.accountSid}
              onChange={(event) => {
                setTwilio((previous) => ({ ...previous, accountSid: event.target.value }));
              }}
            />
          </div>
          <div>
            <label className="lg-label" htmlFor="twilio-token">
              Auth token
            </label>
            <input
              id="twilio-token"
              name="twilio-auth-token"
              type="password"
              className="lg-input"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              placeholder={settings.chat.twilio?.configured ? '••••••••' : ''}
              value={twilio.authToken}
              onChange={(event) => {
                setTwilio((previous) => ({ ...previous, authToken: event.target.value }));
              }}
            />
          </div>
          <div>
            <label className="lg-label" htmlFor="twilio-phone">
              Phone number
            </label>
            <input
              id="twilio-phone"
              name="twilio-phone-number"
              className="lg-input"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              placeholder="+15551234567"
              value={twilio.phoneNumber}
              onChange={(event) => {
                setTwilio((previous) => ({ ...previous, phoneNumber: event.target.value }));
              }}
            />
          </div>
        </div>

        <SaveRow
          pending={saveTwilio.isPending}
          saved={twilioSaved}
          error={twilioError}
          onSave={() => {
            saveTwilio.mutate();
          }}
        />
      </Section>

      <Section
        title="Inbound email addresses"
        description="Mail sent to these addresses opens or continues a conversation."
        actions={
          <button
            type="button"
            className="lg-btn-secondary"
            onClick={() => {
              setInboundOpen(true);
            }}
          >
            <PlusIcon className="h-4 w-4" />
            Add address
          </button>
        }
      >
        {emails.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : (emails.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No inbound addresses yet" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {(emails.data?.items ?? []).map((item) => (
              <li key={item.id} className="flex items-center gap-3 py-2.5">
                <span className="flex-1 font-mono text-sm text-slate-800 dark:text-slate-100">
                  {item.address}
                </span>
                <button
                  type="button"
                  className="lg-btn-ghost px-2 hover:text-rose-600"
                  onClick={() => {
                    removeEmail.mutate(item.id);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Facebook Messenger"
        description="Answer Messenger conversations from the pages you administer, in the same inbox as everything else."
        actions={
          facebook.data?.configured ? (
            <button
              type="button"
              className="lg-btn-secondary"
              disabled={connectFacebookPages.isPending}
              onClick={() => {
                connectFacebookPages.mutate();
              }}
            >
              {connectFacebookPages.isPending ? <Spinner /> : <PlusIcon className="h-4 w-4" />}
              {(facebook.data.items.length ?? 0) === 0 ? 'Connect a page' : 'Add or refresh pages'}
            </button>
          ) : null
        }
      >
        {facebookNote ? (
          <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            {facebookNote}
          </p>
        ) : null}

        {facebook.isLoading ? (
          <Spinner className="h-5 w-5 text-brand-600" />
        ) : !facebook.data?.configured ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Messenger is not configured on this server. Set FACEBOOK_APP_ID and FACEBOOK_APP_SECRET to
            enable it.
          </p>
        ) : (facebook.data.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No pages connected"
            description="Connect a page to answer Messenger conversations here."
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {facebook.data.items.map((page) => (
              <li key={page.id} className="flex items-center gap-3 py-2.5">
                <Avatar src={page.picture} name={page.title} size={32} />
                <span className="flex-1 text-sm text-slate-800 dark:text-slate-100">{page.title}</span>
                <button
                  type="button"
                  className="lg-btn-ghost px-2 hover:text-rose-600"
                  onClick={() => {
                    void confirm({
                      title: `Disconnect ${page.title}?`,
                      message:
                        'Messenger conversations from this page stop arriving. Existing ones stay.',
                      confirmLabel: 'Disconnect',
                      destructive: true,
                    }).then((ok) => {
                      if (ok) removePage.mutate(page.id);
                    });
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="sr-only">Disconnect</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Modal
        open={inboundOpen}
        title="Add an inbound address"
        description="Mail sent here opens a conversation, or continues one it already belongs to."
        onClose={() => {
          setInboundOpen(false);
          addEmail.reset();
        }}
        footer={
          <>
            <button
              type="button"
              className="lg-btn-secondary"
              onClick={() => {
                setInboundOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="inbound-form"
              className="lg-btn-primary"
              disabled={addEmail.isPending || !inbound.username.trim() || !inbound.domain.trim()}
            >
              {addEmail.isPending ? <Spinner /> : null}
              Add address
            </button>
          </>
        }
      >
        <form
          id="inbound-form"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            addEmail.mutate();
          }}
        >
          {saveError(addEmail.error) ? (
            <p
              className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
              role="alert"
            >
              {saveError(addEmail.error)}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="lg-label" htmlFor="inbound-user">
                Mailbox
              </label>
              <input
                id="inbound-user"
                className="lg-input"
                placeholder="parts"
                autoComplete="off"
                value={inbound.username}
                onChange={(event) => {
                  setInbound((previous) => ({ ...previous, username: event.target.value }));
                }}
              />
            </div>

            <div>
              <label className="lg-label" htmlFor="inbound-domain">
                Domain
              </label>
              <input
                id="inbound-domain"
                className="lg-input"
                placeholder="example.com"
                autoComplete="off"
                value={inbound.domain}
                onChange={(event) => {
                  setInbound((previous) => ({ ...previous, domain: event.target.value }));
                }}
              />
            </div>
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400">
            Mail reaches this only if the domain's MX records point at this server, which is a change
            you make where the domain is hosted, not here.
          </p>

          {inbound.username.trim() && inbound.domain.trim() ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Adds{' '}
              <code className="lg-code">
                {inbound.username.trim()}@{inbound.domain.trim()}
              </code>
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
